import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useContractorsPlannedHoursWizardColumnOverrides } from "@/modules/contractorsPlannedHoursWizard/hooks/useContractorsPlannedHoursWizardColumnOverrides";
import { useContractorsPlannedHoursWizardQuery } from "@/modules/contractorsPlannedHoursWizard/hooks/useContractorsPlannedHoursWizardQuery";
import { useSelectionRevisionFromStore } from "@/modules/contractorsPlannedHoursWizard/hooks/useSelectionRevisionFromStore";
import { useSyncedRowSelectionFromStore } from "@/modules/contractorsPlannedHoursWizard/hooks/useSyncedRowSelectionFromStore";
import { useAllocationsListViewStore } from "@/modules/contractorsPlannedHoursWizard/stores/allocationsListViewStore";
import { displayContractorsPlannedHoursWizardAllocation } from "@/schemas/contractorsPlannedHoursWizard";
import {
  dataTableOnlySelectedViewModes,
  DataTableFeatures,
} from "@/types/dataTableSettings";
import { DisplayContractorsPlannedHoursWizardAllocation } from "@/types/contractorsPlannedHoursWizard";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export function ContractorsPlannedHoursWizardAllocationsList({
  columnOverrides,
}: {
  columnOverrides: ReturnType<
    typeof useContractorsPlannedHoursWizardColumnOverrides
  >;
}) {
  const { t } = useTranslation();
  const view = useAllocationsListViewStore((s) => s.view);
  const setView = useAllocationsListViewStore((s) => s.setView);
  const isOnlySelectedView = view === dataTableOnlySelectedViewModes.selected;
  const { query: infiniteQuery, options } =
    useContractorsPlannedHoursWizardQuery(view);
  const { store } = useRowSelectionContext();
  const selectionRevision = useSelectionRevisionFromStore(store);
  const { rowSelection, setRowSelection } =
    useSyncedRowSelectionFromStore(store);
  const queryValue = useMemo(() => ({ query: infiniteQuery }), [infiniteQuery]);
  const allocationsFeatures = useMemo(
    (): DataTableFeatures => ({
      enableRowSelection: true,
      enableSelectAll: true,
      enableSorting: true,
      enableColumnPinning: true,
      enableColumnOrder: true,
      initialColumnVisibility: { id: false },
      enableOnlySelectedFilter: true,
      onlySelectedViewMode: view,
      enableFilters: view === dataTableOnlySelectedViewModes.all,
    }),
    [view],
  );
  return (
    <QueryTable<DisplayContractorsPlannedHoursWizardAllocation>
      schema={displayContractorsPlannedHoursWizardAllocation}
      name="contractorsPlannedHoursWizardAllocations"
      translationKey="contractorsPlannedHoursWizard"
      query={infiniteQuery}
      options={options}
      tableFeatures={allocationsFeatures}
      columnOverrides={columnOverrides}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      getSelection={() => store.getSelection()}
      selectionRevision={selectionRevision}
      snapshotTableName="contractorsPlannedHoursWizardAllocations"
    >
      <DataTableQueryContextProvider value={queryValue}>
        <div className="flex min-h-0 flex-1 flex-col gap-1">
          <QueryTable.Menubar className="mb-0 min-w-0 w-full shrink-0">
            <QueryTable.Menubar.Columns />
            <QueryTable.Menubar.Settings />
            <div className={cn("inline-flex items-center")}>
              <Button
                variant={isOnlySelectedView ? "secondary" : "ghost"}
                size="sm"
                aria-pressed={isOnlySelectedView}
                aria-label={t("table.view.onlySelected")}
                onClick={() =>
                  setView(
                    isOnlySelectedView
                      ? dataTableOnlySelectedViewModes.all
                      : dataTableOnlySelectedViewModes.selected,
                  )
                }
                className="h-8 px-2"
              >
                {t("table.view.onlySelected")}
              </Button>
            </div>
          </QueryTable.Menubar>
          <QueryTable.Content className="min-h-0 flex-1 flex-col">
            <div className="flex min-h-14 w-full shrink-0 items-center">
              <QueryTable.Status>
                <QueryTable.SelectedRowsStatus />
              </QueryTable.Status>
            </div>
          </QueryTable.Content>
        </div>
      </DataTableQueryContextProvider>
    </QueryTable>
  );
}
